import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Alert, CircularProgress, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper
} from '@mui/material';
import { Send, AccountBalanceWallet } from '@mui/icons-material';
import escrowService from '../services/escrowService';
import projectService from '../services/projectService';
import propertyCheckService from '../services/propertyCheckService';
import api from '../services/api';
import notificationService from '../services/notificationService';
import { brand, text } from '../theme/colors';
import PaymentStatsCards from '../components/payments/PaymentStatsCards';
import SmartPaymentAssistant from '../components/payments/SmartPaymentAssistant';
import PaymentAnalytics from '../components/payments/PaymentAnalytics';
import ImprovedPaymentTable from '../components/payments/ImprovedPaymentTable';
import SmartPaymentActions from '../components/payments/SmartPaymentActions';
import NewPaymentForm from '../components/payments/NewPaymentForm';
import ProjectMoneyTrail from '../components/payments/ProjectMoneyTrail';
import EscrowSetupDialog from '../components/escrow/EscrowSetupDialog';

/**
 * Modern Payments Page
 * Redesigned with AI integration, analytics, and improved UX
 */
const ModernPaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeDraftId, setResumeDraftId] = useState(null);
  const [fundWalletDialogOpen, setFundWalletDialogOpen] = useState(false);
  const [fundWalletProject, setFundWalletProject] = useState(null);
  
  const { user } = useAuth();
  const userId = user?.id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get('project') || '';

  // Map escrow data to payment format
  const mapEscrowToPayment = useCallback((escrow, project) => {
    const primaryMilestone = escrow?.milestones?.find(m => m.status !== 'released') || escrow?.milestones?.[0];

    const projectTitle = project?.title
      || escrow.project_title
      || primaryMilestone?.description
      || `Project ${escrow.project_id}`;
    
    const escrowPurpose = escrow.purpose ? ` - ${escrow.purpose}` : '';
    const fullProjectTitle = projectTitle + escrowPurpose;

    const contractorName = project?.contractor?.name
      || project?.contractor_name
      || project?.contractor
      || 'Assigned Contractor';

    const statusMap = {
      created: 'pending',
      funded: 'escrow',
      active: 'escrow',
      completed: 'completed',
      disputed: 'disputed',
      cancelled: 'cancelled'
    };

    const paymentStatus = statusMap[escrow.status] || escrow.status;

    // Check if milestone is completed (ready to release)
    const milestoneCompleted = primaryMilestone?.status === 'completed' || 
                               escrow.milestones?.some(m => m.status === 'completed');

    return {
      id: escrow.id,
      created_at: escrow.created_at,
      amount: escrow.total_amount,
      currency: escrow.currency || 'USD',
      status: paymentStatus,
      contractor_name: contractorName,
      project_name: fullProjectTitle,
      project_id: escrow.project_id,
      description: escrow.purpose || primaryMilestone?.description || 'Payment',
      milestone_completed: milestoneCompleted,
      milestones: escrow.milestones || [],
      reference: escrow.funding_reference,
      due_date: primaryMilestone?.due_date,
      raw: escrow
    };
  }, []);

  // Map a Property Check (standalone verification purchase) into a payment row.
  // Property Checks are paid via Stripe Checkout against verification_requests,
  // not escrow accounts, so they don't come back from getUserEscrows().
  const mapPropertyCheckToPayment = useCallback((pc) => {
    const statusMap = {
      paid: 'completed',      // charged successfully (verification in progress)
      released: 'completed',  // charged & paid out to the verifier
      completed: 'completed',
      refunded: 'refunded',
      refund_pending: 'pending',
      refund_failed: 'failed',
    };
    const loc = pc.location || {};
    const address = loc.address
      || [loc.city, loc.province].filter(Boolean).join(', ')
      || loc.name
      || 'Property Check';
    return {
      id: pc.id,
      created_at: pc.createdAt,
      amount: Number(pc.payment?.amount || 0),
      currency: pc.payment?.currency || 'USD',
      status: statusMap[pc.payment?.status] || pc.payment?.status || 'completed',
      contractor_name: 'Property Check',
      project_name: pc.package?.displayName || 'Property Check',
      project_id: null,
      description: address,
      milestone_completed: false,
      milestones: [],
      reference: pc.id,
      payment_type: 'property_check',
      payment_method: pc.payment?.method || 'stripe_checkout',
      raw: pc,
    };
  }, []);

  // Load payments and projects
  const loadPayments = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');

    try {
      const [escrows, userProjects, propertyChecks] = await Promise.all([
        escrowService.getUserEscrows('all'),
        projectService.getProjects(userId),
        propertyCheckService.listMine().catch(() => [])
      ]);

      const normalizedProjects = Array.isArray(userProjects?.data?.projects)
        ? userProjects.data.projects
        : Array.isArray(userProjects?.projects)
          ? userProjects.projects
          : Array.isArray(userProjects)
            ? userProjects
            : [];

      const projectIndex = new Map(
        normalizedProjects.map(project => [project.id ?? project.projectId ?? project.project_id, project])
      );

      const escrowPayments = Array.isArray(escrows)
        ? escrows.map(escrow => mapEscrowToPayment(escrow, projectIndex.get(escrow.project_id)))
        : [];

      // Include only Property Checks the owner was actually charged for
      // (paid, or paid-then-refunded) — not unpaid/abandoned bookings.
      const chargedStatuses = ['paid', 'released', 'completed', 'refunded', 'refund_pending', 'refund_failed'];
      const propertyCheckPayments = Array.isArray(propertyChecks)
        ? propertyChecks
            .filter(pc => chargedStatuses.includes(pc.payment?.status) && Number(pc.payment?.amount || 0) > 0)
            .map(mapPropertyCheckToPayment)
        : [];

      const paymentsData = [...escrowPayments, ...propertyCheckPayments].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );

      setPayments(paymentsData);
      setProjects(normalizedProjects);
    } catch (err) {
      console.error('Failed to load payments:', err);
      setError(err?.response?.data?.error || err.message || 'Failed to load payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [mapEscrowToPayment, mapPropertyCheckToPayment, userId]);

  useEffect(() => {
    loadPayments();
    loadAnalytics();
  }, [loadPayments]);

  // Subscribe to real-time payment-released events
  useEffect(() => {
    const unsub = notificationService.on('payment-released', () => {
      loadPayments();
    });
    return unsub;
  }, [loadPayments]);

  // Load analytics data from backend
  const loadAnalytics = async () => {
    try {
      const response = await api.get('/payment-analytics/trends');
      if (response.data.success) {
        setAnalyticsData(response.data.data);
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      // Don't show error to user, use fallback data
    }
  };

  // Calculate stats for PaymentStatsCards (matching dashboard calculation)
  const paymentStats = useMemo(() => {
    // Wallet/escrow cards reflect project escrow funds only — Property Check
    // purchases aren't escrow and must not inflate these figures.
    const escrowPayments = payments.filter((p) => p.payment_type !== 'property_check');
    // Total funded in all escrow accounts
    const totalFunded = escrowPayments.reduce((sum, p) => {
      const escrow = p.raw || {};
      return sum + parseFloat(escrow.total_amount || p.amount || 0);
    }, 0);

    // Total released (milestones + materials)
    const totalReleased = escrowPayments.reduce((sum, p) => {
      const escrow = p.raw || {};
      const releasedAmount = parseFloat(escrow.released_amount || 0);
      const releasedMaterialAmount = parseFloat(escrow.released_material_amount || 0);
      return sum + releasedAmount + releasedMaterialAmount;
    }, 0);

    // Funds still in escrow (not released) - matches dashboard calculation
    const fundsInEscrow = totalFunded - totalReleased;

    // Funds held in material orders
    const fundsHeld = escrowPayments.reduce((sum, p) => {
      const escrow = p.raw || {};
      return sum + parseFloat(escrow.held_amount || 0);
    }, 0);

    // Available balance (in escrow but not held)
    const availableBalance = fundsInEscrow - fundsHeld;

    // Pending releases (milestones completed but not released)
    const pendingReleases = escrowPayments
      .filter(p => p.status === 'pending' && p.milestone_completed)
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    const pendingCount = escrowPayments.filter(p => p.status === 'pending' && p.milestone_completed).length;

    // Completed payments
    const completedPayments = escrowPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

    const completedCount = escrowPayments.filter(p => p.status === 'completed').length;

    // Use real analytics data if available, otherwise use calculated values
    if (analyticsData?.stats) {
      return analyticsData.stats;
    }

    // Return calculated stats (now matching dashboard logic)
    return {
      totalEscrow: fundsInEscrow,        // Changed: now shows net remaining (matches dashboard)
      totalFunded,                        // New: total funded amount
      fundsInEscrow,                      // New: same as totalEscrow for clarity
      totalReleased,                      // New: total released amount
      fundsHeld,                          // New: held in material orders
      availableBalance,                   // Fixed: now correctly calculated
      availablePercentage: totalFunded > 0 ? Math.round((availableBalance / totalFunded) * 100) : 0,
      pendingReleases,
      pendingCount,
      completedPayments,
      completedCount,
      escrowTrend: 0,
      balanceTrend: 0,
      pendingTrend: 0,
      completedTrend: 0
    };
  }, [payments, analyticsData]);

  // Handle card clicks for filtering
  const handleCardClick = (cardId) => {
    console.log('Card clicked:', cardId);
    // This could trigger filtering in ImprovedPaymentTable
    // For now, we'll just log it
  };

  // Handle URL filter parameters
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      console.log('Apply filter:', filter);
      // Apply filter to table
    }
  }, [searchParams]);

  const displayedPayments = useMemo(() => {
    if (!selectedProjectId) return payments;
    return payments.filter(payment => payment.project_id === selectedProjectId);
  }, [payments, selectedProjectId]);

  const handleMoneyTrailProjectChange = (projectId) => {
    const nextParams = new URLSearchParams(searchParams);
    if (projectId) {
      nextParams.set('project', projectId);
    } else {
      nextParams.delete('project');
    }
    setSearchParams(nextParams);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: brand.primary }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            💰 Payments
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>
            Manage wallet, releases, and payment analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="text"
            onClick={() => navigate('/disputes')}
            sx={{ textTransform: 'none', fontWeight: 600, color: text.secondary }}
          >
            Disputes
          </Button>
          <Button
            variant="outlined"
            startIcon={<AccountBalanceWallet />}
            onClick={() => setFundWalletDialogOpen(true)}
            sx={{
              borderColor: brand.primary,
              color: brand.primary,
              '&:hover': { bgcolor: brand.primary, color: '#fff' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Fund Wallet
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => setWizardOpen(true)}
            sx={{
              bgcolor: brand.primary,
              '&:hover': { bgcolor: brand.primaryDark },
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            New Payment
          </Button>
        </Box>
      </Box>

      {/* AI Payment Assistant */}
      <Box sx={{ mb: 3 }}>
        <SmartPaymentAssistant />
      </Box>

      {/* Stats Cards */}
      <PaymentStatsCards stats={paymentStats} onCardClick={handleCardClick} />

      <ProjectMoneyTrail
        projects={projects}
        initialProjectId={selectedProjectId}
        onProjectChange={handleMoneyTrailProjectChange}
      />

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column - Analytics & Table */}
        <Grid item xs={12} lg={9}>
          {/* Payment Analytics */}
          <PaymentAnalytics analyticsData={analyticsData} />

          {/* Payment Table */}
          <ImprovedPaymentTable payments={displayedPayments} />
        </Grid>

        {/* Right Column - Quick Actions */}
        <Grid item xs={12} lg={3}>
          <SmartPaymentActions 
            onActionComplete={(action, draftId) => {
              if (action === 'open_wizard') {
                setResumeDraftId(draftId || null);
                setWizardOpen(true);
              } else {
                loadPayments();
              }
            }} 
            refreshTrigger={wizardOpen} // Trigger refresh when wizard closes
          />
        </Grid>
      </Grid>

      {/* New Payment Form */}
      <NewPaymentForm
        open={wizardOpen}
        resumeDraftId={resumeDraftId}
        onClose={() => {
          setWizardOpen(false);
          setResumeDraftId(null);
        }}
        onComplete={(result) => {
          setSuccess('Payment created successfully!');
          loadPayments();
          setWizardOpen(false);
          setResumeDraftId(null);
          setTimeout(() => setSuccess(''), 3000);
        }}
      />

      {/* Fund Wallet — Select Project */}
      <Dialog
        open={fundWalletDialogOpen}
        onClose={() => setFundWalletDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Fund a Project Wallet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a project to fund its wallet. A project needs an accepted
            quotation first — that links your money to your contractor from day one.
          </Typography>
          {projects.length > 0 ? (
            projects.map((p) => (
              <Paper
                key={p.id}
                elevation={1}
                sx={{
                  p: 2,
                  mb: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': { bgcolor: 'action.hover', elevation: 3 }
                }}
                onClick={() => {
                  setFundWalletDialogOpen(false);
                  setFundWalletProject(p);
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {p.title || p.name || p.id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.status}
                  </Typography>
                </Box>
                <AccountBalanceWallet color="action" />
              </Paper>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No projects found. Create a project first to fund a wallet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFundWalletDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Escrow Setup Dialog — opens directly after project selection */}
      <EscrowSetupDialog
        open={!!fundWalletProject}
        onClose={() => setFundWalletProject(null)}
        project={fundWalletProject}
        skipTerms={!!fundWalletProject?.escrow?.id}
        onSuccess={() => {
          setFundWalletProject(null);
          setSuccess('Wallet funded successfully!');
          loadPayments();
          setTimeout(() => setSuccess(''), 3000);
        }}
      />
    </Container>
  );
};

export default ModernPaymentsPage;
